<template>
  <CustomTable
    v-bind="$attrs"
    v-on="$listeners"
    :tableData="displayTableData"
    :column="innerColumn"
  >
    <!-- 我方自定义的表头筛选 slot -->
    <template
      v-for="col in filterableColumns"
      :slot="headerSlotName(col)"
      slot-scope="{ row }"
    >
      <div :key="col.prop" class="filter-header-wrap">
        <el-popover
          :ref="'pop_' + col.prop"
          placement="bottom"
          trigger="click"
          popper-class="filterable-table-popover"
          @show="handlePopoverShow(col)"
        >
          <div class="filter-panel">
            <!-- 双列 checkbox -->
            <template v-if="col.filterConfig.type === 'checkbox-group'">
              <div class="checkbox-groups">
                <div
                  v-for="g in col.filterConfig.groups"
                  :key="g.key"
                  class="checkbox-group-col"
                >
                  <div class="group-title">{{ g.title }}</div>
                  <el-checkbox-group v-model="draft[col.prop][g.key]">
                    <el-checkbox
                      v-for="opt in g.options"
                      :key="opt.value"
                      :label="opt.value"
                    >{{ opt.label }}</el-checkbox>
                  </el-checkbox-group>
                </div>
              </div>
            </template>

            <!-- 数值范围 -->
            <template v-else-if="col.filterConfig.type === 'range'">
              <div class="range-row">
                <el-input
                  v-model="draft[col.prop].min"
                  size="small"
                  placeholder="最低"
                  class="range-input"
                >
                  <template slot="suffix" v-if="col.filterConfig.unit">{{ col.filterConfig.unit }}</template>
                </el-input>
                <span class="range-sep">-</span>
                <el-input
                  v-model="draft[col.prop].max"
                  size="small"
                  placeholder="最高"
                  class="range-input"
                >
                  <template slot="suffix" v-if="col.filterConfig.unit">{{ col.filterConfig.unit }}</template>
                </el-input>
              </div>
            </template>

            <!-- 单列 checkbox -->
            <template v-else-if="col.filterConfig.type === 'checkbox'">
              <el-checkbox-group v-model="draft[col.prop]" class="checkbox-single">
                <el-checkbox
                  v-for="opt in col.filterConfig.options"
                  :key="opt.value"
                  :label="opt.value"
                >{{ opt.label }}</el-checkbox>
              </el-checkbox-group>
            </template>

            <!-- 下拉单选 -->
            <template v-else-if="col.filterConfig.type === 'select'">
              <el-select
                v-model="draft[col.prop]"
                size="small"
                placeholder="请选择"
                clearable
                class="select-full"
              >
                <el-option
                  v-for="opt in col.filterConfig.options"
                  :key="opt.value"
                  :label="opt.label"
                  :value="opt.value"
                />
              </el-select>
            </template>

            <!-- 日期范围 -->
            <template v-else-if="col.filterConfig.type === 'daterange'">
              <el-date-picker
                v-model="draft[col.prop]"
                type="daterange"
                size="small"
                value-format="yyyy-MM-dd"
                range-separator="至"
                start-placeholder="开始日期"
                end-placeholder="结束日期"
                class="select-full"
              />
            </template>

            <div class="panel-footer">
              <span class="btn-confirm" @click="handleConfirm(col)">筛选</span>
              <i class="el-icon-refresh btn-reset" @click="handleReset(col)"></i>
            </div>
          </div>

          <span
            slot="reference"
            class="filter-header-ref"
            :class="{ 'is-active': hasValue(col.prop) }"
          >
            {{ row.label }}
            <i class="el-icon-arrow-down arrow-icon"></i>
          </span>
        </el-popover>
      </div>
    </template>

    <!-- 透传父层所有 body scoped slot -->
    <template
      v-for="(_, name) in $scopedSlots"
      :slot="name"
      slot-scope="scope"
    >
      <slot :name="name" v-bind="scope" />
    </template>
  </CustomTable>
</template>

<script>
export default {
  name: 'CustomFilterableTable',
  inheritAttrs: false,
  props: {
    tableData: { type: Array, default: () => [] },
    column: { type: Array, default: () => [] },
    // v-model 的筛选状态 { [prop]: value }
    value: { type: Object, default: () => ({}) },
    // 过滤模式：'frontend' 前端过滤（默认） | 'backend' 后端接口过滤
    filterMode: { type: String, default: 'frontend', validator: (val) => ['frontend', 'backend'].includes(val) },
    // 总数据条数（后端分页时使用）
    total: { type: Number, default: 0 }
  },
  data() {
    return {
      // 面板未点"筛选"前的草稿值
      draft: {}
    }
  },
  computed: {
    filterableColumns() {
      return this.column.filter(c => c && c.filterConfig)
    },
    innerColumn() {
      return this.column.map(col => {
        if (col.filterConfig) {
          return { ...col, slotHeader: this.headerSlotName(col) }
        }
        return col
      })
    },
    // 用于展示的表格数据（前端过滤模式下使用）
    displayTableData() {
      if (this.filterMode === 'backend') {
        // 后端模式：直接使用传入的 tableData
        return this.tableData
      }
      // 前端模式：已经在父组件中处理过过滤逻辑
      return this.tableData
    }
  },
  created() {
    this.initDraft()
  },
  watch: {
    value: {
      deep: true,
      handler() {
        this.initDraft()
      }
    },
    column() {
      this.initDraft()
    }
  },
  methods: {
    headerSlotName(col) {
      return '__headerFilter_' + col.prop
    },
    emptyValue(type) {
      if (type === 'checkbox-group') return {}
      if (type === 'range') return { min: '', max: '' }
      if (type === 'checkbox') return []
      if (type === 'daterange') return []
      return ''
    },
    initDraft() {
      const draft = {}
      this.filterableColumns.forEach(col => {
        const t = col.filterConfig.type
        const cur = this.value[col.prop]
        if (t === 'checkbox-group') {
          const obj = {}
          ;(col.filterConfig.groups || []).forEach(g => {
            obj[g.key] = (cur && Array.isArray(cur[g.key])) ? [...cur[g.key]] : []
          })
          draft[col.prop] = obj
        } else if (t === 'range') {
          draft[col.prop] = {
            min: cur && cur.min != null ? cur.min : '',
            max: cur && cur.max != null ? cur.max : ''
          }
        } else if (t === 'checkbox') {
          draft[col.prop] = Array.isArray(cur) ? [...cur] : []
        } else if (t === 'daterange') {
          draft[col.prop] = Array.isArray(cur) ? [...cur] : []
        } else {
          draft[col.prop] = cur != null ? cur : ''
        }
      })
      this.draft = draft
    },
    hasValue(prop) {
      const v = this.value[prop]
      if (v == null) return false
      if (Array.isArray(v)) return v.length > 0
      if (typeof v === 'object') {
        if ('min' in v || 'max' in v) return v.min !== '' || v.max !== ''
        return Object.values(v).some(x => Array.isArray(x) ? x.length > 0 : !!x)
      }
      return v !== ''
    },
    isDraftEmpty(col) {
      const t = col.filterConfig.type
      const d = this.draft[col.prop]
      if (t === 'checkbox-group') return !Object.values(d || {}).some(x => Array.isArray(x) && x.length > 0)
      if (t === 'range') return !d || (d.min === '' && d.max === '')
      if (t === 'checkbox') return !Array.isArray(d) || d.length === 0
      if (t === 'daterange') return !Array.isArray(d) || d.length === 0
      return d === '' || d == null
    },
    handlePopoverShow(col) {
      // 打开面板时同步当前 value 到草稿
      this.initDraft()
    },
    closePopover(col) {
      const ref = this.$refs['pop_' + col.prop]
      const pop = Array.isArray(ref) ? ref[0] : ref
      if (pop && typeof pop.doClose === 'function') pop.doClose()
    },
    handleConfirm(col) {
      const next = { ...this.value }
      if (this.isDraftEmpty(col)) {
        delete next[col.prop]
      } else {
        // 深拷贝当前草稿快照
        const d = this.draft[col.prop]
        next[col.prop] = JSON.parse(JSON.stringify(d))
      }
      this.$emit('input', next)
      
      // 根据过滤模式触发不同的事件
      if (this.filterMode === 'backend') {
        // 后端模式：触发后端过滤事件，父组件监听后调用 API
        this.$emit('backend-filter-change', { prop: col.prop, value: next[col.prop], all: next })
      } else {
        // 前端模式：触发前端过滤事件
        this.$emit('filter-change', { prop: col.prop, value: next[col.prop], all: next })
      }
      this.closePopover(col)
    },
    handleReset(col) {
      this.draft = {
        ...this.draft,
        [col.prop]: this.emptyValue(col.filterConfig.type) === '{}' ? {} : this.freshDraft(col)
      }
      // 同步 reset 到 value
      const next = { ...this.value }
      delete next[col.prop]
      this.$emit('input', next)
      
      // 根据过滤模式触发不同的事件
      if (this.filterMode === 'backend') {
        // 后端模式：触发后端过滤事件
        this.$emit('backend-filter-change', { prop: col.prop, value: undefined, all: next })
      } else {
        // 前端模式：触发前端过滤事件
        this.$emit('filter-change', { prop: col.prop, value: undefined, all: next })
      }
      this.closePopover(col)
    },
    freshDraft(col) {
      const t = col.filterConfig.type
      if (t === 'checkbox-group') {
        const obj = {}
        ;(col.filterConfig.groups || []).forEach(g => { obj[g.key] = [] })
        return obj
      }
      if (t === 'range') return { min: '', max: '' }
      if (t === 'checkbox') return []
      if (t === 'daterange') return []
      return ''
    }
  },
  components: {
    CustomTable: () => import('@/components/CustomTable.vue')
  }
}
</script>

<style scoped lang="less">
.filter-header-wrap {
  display: inline-flex;
  align-items: center;
}

.filter-header-ref {
  display: inline-flex;
  align-items: center;
  gap: 4px;
  cursor: pointer;
  user-select: none;
  color: #5A6678;
  font-weight: 500;

  .arrow-icon {
    font-size: 12px;
    color: #8A94A6;
    transition: color 0.2s;
  }

  &:hover,
  &.is-active {
    color: #1890FF;

    .arrow-icon {
      color: #1890FF;
    }
  }
}
</style>

<style lang="less">
/* 全局面板样式（popper 渲染到 body） */
.filterable-table-popover.el-popover {
  padding: 12px 14px;
  min-width: 180px;
  border-radius: 6px;

  .filter-panel {
    display: flex;
    flex-direction: column;
    gap: 10px;
  }

  .checkbox-groups {
    display: flex;
    gap: 18px;
  }

  .checkbox-group-col {
    display: flex;
    flex-direction: column;

    .group-title {
      font-size: 13px;
      font-weight: 600;
      color: #1F2329;
      margin-bottom: 6px;
      padding: 4px 8px;
    }

    .el-checkbox {
      display: block;
      margin: 4px 0 4px 4px;
    }
  }

  .checkbox-single {
    display: flex;
    flex-direction: column;

    .el-checkbox {
      margin: 4px 0;
    }
  }

  .range-row {
    display: flex;
    align-items: center;
    gap: 6px;

    .range-input {
      width: 96px;

      .el-input__suffix {
        display: inline-flex;
        align-items: center;
        justify-content: center;
        height: 100%;
        top: 0;
        right: 6px;
        color: #8A94A6;
        font-size: 13px;
        line-height: 1;
      }

      .el-input__suffix-inner {
        display: inline-flex;
        align-items: center;
        height: 100%;
      }

      .el-input__inner {
        padding-right: 26px;
      }
    }

    .range-sep {
      color: #8A94A6;
    }
  }

  .select-full {
    width: 200px;
  }

  /* 日期范围选择器加宽，避免遮挡 */
  .el-date-editor.el-date-editor--daterange.el-input__inner {
    width: 260px !important;
  }

  .panel-footer {
    display: flex;
    justify-content: center;
    align-items: center;
    gap: 12px;
    padding-top: 8px;
    border-top: 1px solid #EBEEF2;

    .btn-confirm {
      color: #1890FF;
      font-size: 13px;
      cursor: pointer;

      &:hover { color: #40A9FF; }
    }

    .btn-reset {
      color: #8A94A6;
      font-size: 14px;
      cursor: pointer;

      &:hover { color: #1890FF; }
    }
  }
}
</style>
